import { useEffect, useState } from "react";
import { collection, onSnapshot, query, where, doc, updateDoc } from "firebase/firestore";
import { db } from "../lib/firebase";
import { Sidebar } from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

// Formata DD/MM/YYYY a partir de um JS Date usando valores UTC,
// evitando o bug clássico de timezone: Timestamp salvo como meia-noite
// no fuso do dispositivo (ex: 00:00 UTC-2 = 02:00 UTC), que ao ser lido
// em UTC-3 vira 23:00 do dia anterior via getDate().
const formatDateBR = (date) => {
  if (!date) return null;
  const d = String(date.getUTCDate()).padStart(2, "0");
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const y = date.getUTCFullYear();
  return `${d}/${m}/${y}`;
};

// Calcula idade usando a data UTC do nascimento vs. data local de hoje
const calcAge = (date) => {
  if (!date) return null;
  const today = new Date();
  let age = today.getFullYear() - date.getUTCFullYear();
  if (
    today.getMonth() < date.getUTCMonth() ||
    (today.getMonth() === date.getUTCMonth() && today.getDate() < date.getUTCDate())
  ) {
    age--;
  }
  return age;
};

// Remove acentos e normaliza para busca case-insensitive
const normalize = (str) =>
  (str ?? "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();

export const AdminOrganizers = () => {
  const [organizers, setOrganizers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const q = query(
      collection(db, 'users'),
      where('organizerStatus', 'in', ['pending', 'approved', 'rejected'])
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setOrganizers(list);
      setLoading(false);
    }, (error) => {
      console.error('[AdminOrganizers] Erro no listener de usuários:', error);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const approve = async (id) => {
    await updateDoc(doc(db, "users", id), {
      organizerStatus: "approved",
    });

    toast.success("Organizador aprovado");
  };

  const reject = async (id) => {
    await updateDoc(doc(db, "users", id), {
      organizerStatus: "rejected",
    });

    toast.success("Organizador rejeitado");
  };

  // 📊 STATS
  const stats = {
    total: organizers.length,
    pending: organizers.filter((u) => u.organizerStatus === "pending").length,
    approved: organizers.filter((u) => u.organizerStatus === "approved").length,
    rejected: organizers.filter((u) => u.organizerStatus === "rejected").length,
  };

  // 🔍 FILTRO por status
  const filtered = organizers
    .filter((u) => {
      if (filter === "all") return true;
      return u.organizerStatus === filter;
    })
    .sort((a, b) => {
      const aDate = a.createdAt?.toDate?.() || new Date(0);
      const bDate = b.createdAt?.toDate?.() || new Date(0);
      return bDate - aDate;
    });

  // 🔎 BUSCA em tempo real (aplicada sobre filtered)
  const searchTerm = normalize(search);
  const displayed = searchTerm
    ? filtered.filter((u) =>
        [u.realName, u.firstName, u.lastName, u.email, u.phone].some((f) =>
          normalize(f).includes(searchTerm)
        )
      )
    : filtered;

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />

      <main className="flex-1 p-4 pt-16 md:p-8 md:ml-72 space-y-8">
        <h1 className="text-3xl font-bold">Organizadores</h1>

        {/* 📊 CARDS */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard title="Total" value={stats.total} />
          <StatCard title="Pendentes" value={stats.pending} color="text-yellow-500" />
          <StatCard title="Aprovados" value={stats.approved} color="text-green-600" />
          <StatCard title="Rejeitados" value={stats.rejected} color="text-red-600" />
        </div>

        {/* 🔘 FILTROS + BUSCA */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex gap-2 flex-wrap">
            {["all", "pending", "approved", "rejected"].map((f) => (
              <Button
                key={f}
                variant={filter === f ? "default" : "outline"}
                onClick={() => setFilter(f)}
              >
                {f === "all" && "Todos"}
                {f === "pending" && "Pendentes"}
                {f === "approved" && "Aprovados"}
                {f === "rejected" && "Rejeitados"}
              </Button>
            ))}
          </div>

          <div className="relative sm:ml-auto">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
            </svg>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar organizadores..."
              className="w-full sm:w-72 pl-9 pr-4 py-2 text-sm border border-input rounded-lg bg-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        </div>

        {/* 📋 LISTA */}
        {loading ? (
          <p>Carregando...</p>
        ) : displayed.length === 0 ? (
          <p>Nenhum organizador encontrado 👤</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {displayed.map((user) => {
              return (
                <div
                  key={user.id}
                  className="border rounded-xl p-4 bg-white flex flex-col justify-between shadow-sm"
                >
                  <div>
                    <p className="font-semibold text-sm mb-1 truncate">
                      {user.realName || "Sem nome"}
                    </p>

                    <p className="text-xs text-muted-foreground truncate">
                      {user.email || "-"}
                    </p>

                    <p className="text-xs text-muted-foreground">
                      📞 {user.phone || "-"}
                    </p>

                    {(() => {
                      const bd = user.birthDate?.toDate?.() ?? null;
                      const age = calcAge(bd);
                      const isMinor = age !== null && age < 18;
                      return (
                        <div className="flex items-center gap-1 flex-wrap mt-0.5">
                          <p className={`text-xs ${isMinor ? "text-red-500 font-semibold" : "text-muted-foreground"}`}>
                            🎂 {formatDateBR(bd) ?? "-"}{age !== null ? ` (${age} anos)` : ""}
                          </p>
                          {isMinor && (
                            <span className="text-[9px] bg-red-100 text-red-600 border border-red-300 rounded px-1 py-0.5 font-bold uppercase leading-none">
                              Menor de idade
                            </span>
                          )}
                        </div>
                      );
                    })()}

                    <p
                      className={`text-[10px] mt-2 font-bold uppercase ${
                        user.organizerStatus === "pending"
                          ? "text-yellow-500"
                          : user.organizerStatus === "approved"
                          ? "text-green-600"
                          : "text-red-500"
                      }`}
                    >
                      {user.organizerStatus}
                    </p>
                  </div>

                  {user.organizerStatus === "pending" && (
                    <div className="flex gap-1 mt-3">
                      <Button
                        size="sm"
                        className="bg-green-600 text-xs px-2 py-1"
                        onClick={() => approve(user.id)}
                      >
                        ✔
                      </Button>

                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-500 text-xs px-2 py-1"
                        onClick={() => reject(user.id)}
                      >
                        ✖
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

// 📦 COMPONENTE DE CARD (STATS)
const StatCard = ({ title, value, color }) => {
  return (
    <div className="bg-card border border-border rounded-xl p-4">
      <p className="text-sm text-muted-foreground">{title}</p>
      <p className={`text-2xl font-bold ${color || ""}`}>{value}</p>
    </div>
  );
};
